import { Link } from '@tanstack/react-router';
import { Moon, Sun } from 'lucide-react';

import type { GapReviewTheme } from '../hooks/useGapReviewTheme';
import { DEFAULT_QUEUE_SEARCH } from '../lib/queue';

interface GapReviewTopbarProps {
  /** Drafts of the selected run still awaiting a verdict — the queue badge. */
  pendingCount: number;
  /**
   * Kept drafts across EVERY run — the final-review badge, counted the way the
   * page behind it lists them. The two badges deliberately have different
   * scopes: the queue works one run, final review works the whole company.
   */
  keptCount: number;
  /**
   * The selected run, which every nav item carries: the queue and final review
   * so that switching views does not silently switch runs, and the run audit
   * because its URL is keyed on the id.
   *
   * Undefined only when the company has no runs at all, which hides the audit
   * item — an audit of nothing is not a page worth reaching.
   */
  executionId: string | undefined;
  /**
   * The keyboard legend only describes the review queue's shortcuts, so it is
   * hidden rather than misleading on the other views.
   */
  showKeyboardHint: boolean;
  theme: GapReviewTheme;
  onToggleTheme: () => void;
}

/**
 * The one strip shared by every gap-review view.
 *
 * Nav items are router links rather than view-switching buttons, so each view
 * has a URL that can be shared and reloaded; `aria-current="page"` is what the
 * router sets on the active one, and what the stylesheet keys on.
 */
export function GapReviewTopbar({
  pendingCount,
  keptCount,
  executionId,
  showKeyboardHint,
  theme,
  onToggleTheme,
}: GapReviewTopbarProps) {
  return (
    <header className="gr-topbar">
      <div className="gr-brand">
        <i />
        Gap review
      </div>

      <nav className="gr-nav">
        {/* `exact` on the pathname but not the search: the queue's own filters
            are search params, so a filtered queue is still the queue.

            The run rides along on both of these. Switching view is not meant to
            switch run, and a link that dropped the execution would silently land
            the reviewer back on the newest one. */}
        <Link
          to="/gap-review"
          search={{ ...DEFAULT_QUEUE_SEARCH, execution: executionId }}
          className="gr-nav-btn"
          activeOptions={{ exact: true, includeSearch: false }}
        >
          Review queue <span>{pendingCount}</span>
        </Link>
        <Link
          to="/gap-review/final"
          search={{ execution: executionId }}
          className="gr-nav-btn"
        >
          Final review <span>{keptCount}</span>
        </Link>
        {executionId !== undefined && (
          <Link
            to="/gap-review/run/$executionId"
            params={{ executionId }}
            className="gr-nav-btn"
          >
            Run audit
          </Link>
        )}
      </nav>

      <div className="gr-topbar-end">
        {showKeyboardHint && (
          <div className="gr-hint">
            <span className="gr-kbd">J</span>
            <span className="gr-kbd">K</span> move
            <span className="gr-kbd">1</span> keep
            <span className="gr-kbd">2</span> drop
            <span className="gr-kbd">C</span> comment
            <span className="gr-kbd">P</span> preview
          </div>
        )}
        <button
          type="button"
          className="gr-icon-btn"
          onClick={onToggleTheme}
          title="Toggle light / dark"
          aria-label="Toggle light or dark theme"
        >
          {theme === 'dark' ? <Sun /> : <Moon />}
        </button>
      </div>
    </header>
  );
}
